import { atLeast, type OperatorRole } from '@lib/operator';

import { parseMemberList } from './component-create';
import { isReleaseVersion } from './release-create';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * The edit dialog's logic as pure functions, so the payload is something a node
 * test can see. An edit can change the version label and the flag list, nothing
 * else: the API refuses `name` and `description` with a 400, so this module has
 * no way to produce them.
 */

export interface ReleaseEditDraft {
    readonly releaseVersion: string;
    /** One flag key per line, in release order. */
    readonly flags: string;
    readonly reason: string;
}

/** The body for `PATCH /release-components/:id`. Only changed fields are present. */
export interface UpdateReleaseInput {
    readonly expectedRowVersion: number;
    readonly releaseVersion?: string;
    readonly flagKeys?: readonly string[];
    readonly reason?: string;
}

export function releaseEditDraftFrom(release: ReleaseComponent): ReleaseEditDraft {
    return {
        releaseVersion: release.releaseVersion,
        flags: release.flagKeys.join('\n'),
        reason: '',
    };
}

/**
 * The edit to send, or `null` while there is nothing to send: nothing changed, the
 * version would be refused, or the page holds no row version to guard with (the
 * edit would be an unguarded overwrite, which the API does not accept either).
 *
 * The flag list is sent whole when it differs at all, as the API replaces the
 * list rather than patching it. Order counts as a difference: it is editorial.
 */
export function releaseEditInputFrom(
    draft: ReleaseEditDraft,
    release: ReleaseComponent,
): UpdateReleaseInput | null {
    if (release.rowVersion === undefined) return null;

    const releaseVersion = draft.releaseVersion.trim();
    if (!isReleaseVersion(releaseVersion)) return null;

    const flagKeys = parseMemberList(draft.flags);
    const versionChanged = releaseVersion !== release.releaseVersion;
    const flagsChanged =
        flagKeys.length !== release.flagKeys.length ||
        flagKeys.some((key, index) => key !== release.flagKeys[index]);
    if (!versionChanged && !flagsChanged) return null;

    const reason = draft.reason.trim();
    return {
        expectedRowVersion: release.rowVersion,
        ...(versionChanged ? { releaseVersion } : {}),
        ...(flagsChanged ? { flagKeys } : {}),
        ...(reason.length > 0 ? { reason } : {}),
    };
}

/** Matches `@RequiresRole('engineer')` on `PATCH /release-components/:id`. */
export function canEditRelease(role: OperatorRole): boolean {
    return atLeast(role, 'engineer');
}

/** Matches `@RequiresRole('engineer')` on `PUT /release-components/:id/archive`. */
export function canArchiveRelease(role: OperatorRole): boolean {
    return atLeast(role, 'engineer');
}
